import { useState, useEffect, useRef } from 'react'
import { useRouter } from '~/hooks/useRouter'
import type { ProductComponent } from '~/components/ProductPage/TableProductPage'
import { ColorName } from '~/utils/colorDictionary'

export type TableMaterial = 'plywood' | 'chipboard'

export const TABLE_HEIGHT = 75

export const TABLE_DEFAULTS = {
  width: 210,
  depth: 80,
  material: 'chipboard' as TableMaterial,
} as const

export const TABLE_WIDTH_RANGE: [number, number] = [210, 270]
export const TABLE_WIDTH_STEP = 10

export const TABLE_DEPTH_OPTIONS = [100, 90, 80] as const

const PLYWOOD_TEXTURES: string[] = [
  // ColorName.NaturalAccacia,
  ColorName.BirchPlywood,
  ColorName.LightOak,
  ColorName.NaturalOak,
  ColorName.DarkOak,
  ColorName.BlackOak,
]

const CHIPBOARD_TEXTURES: string[] = [
  ColorName.LightCasellaOak,
  ColorName.NaturalCasellaOak,
  ColorName.BrownCasellaOak,
  ColorName.MaroneCasellaOak,
]

const TEXTURES_BY_MATERIAL: Record<TableMaterial, string[]> = {
  plywood: PLYWOOD_TEXTURES,
  chipboard: CHIPBOARD_TEXTURES,
}

interface TexturePricing {
  base: number
  k: number
}

const BIRCH_TEXTURES = [ColorName.BirchPlywood]
const OAK_PLYWOOD_TEXTURES = [
  ColorName.LightOak,
  ColorName.NaturalOak,
  ColorName.DarkOak,
  ColorName.BlackOak,
]

function getTexturePricing(texture: string): TexturePricing {
  if (BIRCH_TEXTURES.includes(texture as ColorName)) {
    return { base: 22500, k: 50 }
  }
  if (OAK_PLYWOOD_TEXTURES.includes(texture as ColorName)) {
    return { base: 34000, k: 60 }
  }
  return { base: 21000, k: 50 }
}

export function calculateTablePrice(
  texture: string,
  depth: number,
  width: number
): number {
  const { base, k } = getTexturePricing(texture)
  return base + (depth - 80) * k + (width - 210) * k
}

export const TableConfiguration: () => ProductComponent[] = () => {
  const router = useRouter()
  const hasInitialized = useRef(false)

  const [width, setWidth] = useState<number>(TABLE_DEFAULTS.width)
  const [depth, setDepth] = useState<number>(TABLE_DEFAULTS.depth)
  const [material, setMaterial] = useState<TableMaterial>(
    TABLE_DEFAULTS.material
  )
  const [selectedTexture, setSelectedTexture] = useState<string>(
    CHIPBOARD_TEXTURES[0]
  )

  useEffect(() => {
    if (!router.isReady || hasInitialized.current) return
    hasInitialized.current = true

    const q = router.query
    if (q.width) setWidth(Number(q.width))
    if (q.depth) setDepth(Number(q.depth))

    const mat = q.material === 'plywood' ? 'plywood' : 'chipboard'
    setMaterial(mat)

    const textures = TEXTURES_BY_MATERIAL[mat]
    if (typeof q.texture === 'string' && textures.includes(q.texture)) {
      setSelectedTexture(q.texture)
    } else {
      setSelectedTexture(textures[0])
    }
  }, [router.isReady, router.query])

  useEffect(() => {
    if (!hasInitialized.current) return
    const textures = TEXTURES_BY_MATERIAL[material]
    if (!textures.includes(selectedTexture)) {
      setSelectedTexture(textures[0])
    }
  }, [material])

  return [
    {
      type: 'dimensions',
      width,
      setWidth,
      depth,
      setDepth,
      height: TABLE_HEIGHT,
      plintHeight: 0,
    },
    {
      type: 'material',
      material,
      setMaterial,
    },
    {
      type: 'texture',
      textures: TEXTURES_BY_MATERIAL[material],
      selectedTexture,
      setSelectedTexture,
    },
    {
      type: 'price',
      price: calculateTablePrice(selectedTexture, depth, width),
    },
  ]
}
